import React, { use, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GridPageChangeEvent } from '@progress/kendo-react-grid';
import cn from 'clsx';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import gridStyles from '@components-clean/core/grid/style.module.scss';
import Grid from '@components-clean/core/grid-fe';
import {
    CellWithChildren,
    DateCell,
    MaxLengthCell,
    MultiValueDataCell,
    RagValueCell,
} from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Loading from '@components-clean/core/loading/loading';
import { PopOutPage } from '@components-clean/core/page-toolbar/page-toolbar';
import styles from '@components-clean/core/page-toolbar/styles.module.scss';
import { useStudyDataFilterContext } from '@contexts-clean/study-data-filter-context';
import {
    StudyHubSiteGridPanelData,
    StudyHubSiteGridData,
    StudyHubSiteGridPanelColumnData,
    StudyHubSiteGridWidgetColumnData,
    StudyHubSiteGridWidgetData,
} from '@interfaces/central-monitoring';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { UserContext } from '@contexts/user-context';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { AllPaths } from '@utils/router/all-paths';
import { GET_STUDY_HUB_SITE_FIELDS_CONFIG } from '@queries/central-monitoring';
import {
    getStudyHubSiteFieldsConfig,
    getStudyHubSiteFieldsConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import StudyHubApi from '@apis/bff-api/central-monitoring/study-hub';

const SiteView = () => {
    const { t }: { t: (key: string) => string } = useTranslation('cm', {
        keyPrefix: 'studyHub.siteOverviewTable',
    });
    const { current } = use(StudySiteHubContext);

    const { studyRef } = current;
    const datasetIdInFocus = current.dataset?.id;
    const datasetDateInFocus = stringDateWithoutTimeFromUtc(current.dataset?.date, 'DD-MMM-YYYY')!;
    const comparisonDatasetId = current.datasetFilters.comparisonDataset?.id ?? undefined;
    const comparisonDatasetDate = stringDateWithoutTimeFromUtc(
        current.datasetFilters.comparisonDataset?.dataCutDate ?? null,
        'DD-MMM-YYYY',
    )!;

    const { user } = use(UserContext);
    const { state: localState } = useLocalState();
    const studyName = localState?.selectedStudy?.studyNumber;

    const [skip, setSkip] = useState<number>(0);

    const { sessionId: siteFilterSessionId, filtersChanged$, isFiltered } = useStudyDataFilterContext();
    const {
        data,
        error,
        isMutating: isLoading = true,
        trigger,
    } = StudyHubApi.useGetStudyHubSiteGridData(studyRef, siteFilterSessionId, datasetIdInFocus, comparisonDatasetId);

    const [loadConfig, { data: siteFieldsConfigData }] = useLazyQuery<
        getStudyHubSiteFieldsConfig,
        getStudyHubSiteFieldsConfigVariables
    >(GET_STUDY_HUB_SITE_FIELDS_CONFIG);

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef]);

    useEffect(() => {
        const triggerRequest = () => {
            if (!siteFilterSessionId || !siteFieldsConfigData) {
                return;
            }
            trigger().catch((err) => window.console.log(err));
        };
        triggerRequest();

        // Subscribe to the filter changed event emitted by the Filter UI, and re-trigger the get
        const subscription = filtersChanged$.subscribe(() => {
            triggerRequest();
        });

        // Return a function that unsubscribes from the observable
        return () => subscription.unsubscribe();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef, datasetIdInFocus, siteFilterSessionId, comparisonDatasetId, siteFieldsConfigData]);

    interface ITableInfo {
        columns: GridColumnProps[];
        data: { [key: string]: unknown }[];
    }

    interface IRow {
        siteNumber: string;
    }

    const siteHubRoute = AllPaths.path('cm2-site-hub');

    interface ITableColumnProperties {
        id?: string;
        valueField?: string;
        title?: string | null;
        isNumeric?: boolean;
        isRagColumn?: boolean;
        isRounded?: boolean;
        isDate?: boolean;
        singleColumnMinWidth?: number;
        multiColumnMinWidth?: number;
        show?: boolean;
        isFilterable?: boolean;
        headerClassName?: string;
        className?: string;
        filterDisabled?: boolean;
        filterClassName?: string;
        locked?: boolean;
        lockWidth?: boolean;
        resizable?: boolean;
        maxLength?: number;
    }

    const TableColumnPropertyDefaults: ITableColumnProperties = {
        isNumeric: false,
        isRagColumn: false,
        isRounded: true,
        isDate: false,
        singleColumnMinWidth: 120,
        multiColumnMinWidth: 140,
        show: true,
        isFilterable: true,
        filterDisabled: false,
        locked: false,
        lockWidth: false,
        resizable: true,
    };

    const getTableColumn = (props: ITableColumnProperties): GridColumnProps[] => {
        props = { ...TableColumnPropertyDefaults, ...props };

        if (comparisonDatasetId !== undefined && props.isNumeric) {
            return [
                {
                    id: props.id ? `${props.id}_${props.valueField}` : `${props.valueField}`,
                    title: props.title ?? t(`columns.${props.valueField}`),
                    show: props.show,
                    lockVisibility: true,
                    locked: false,
                    sortable: false,
                    filterable: false,
                    resizable: props.resizable,
                    children: [
                        {
                            id: props.id ? `${props.id}_${props.valueField}_values` : `${props.valueField}_values`,
                            field: props.id ? `${props.id}.${props.valueField}` : props.valueField,
                            title: t('columns.values'),
                            show: props.show,
                            minWidth: props.multiColumnMinWidth,
                            filterKey: 'valueInFocus',
                            comparisonFilterKey: 'comparisonValue',
                            cells: {
                                data: (gridCellProps) =>
                                    MultiValueDataCell({
                                        ...gridCellProps,
                                        isVirtualized: true,
                                        highlighted: props.isRagColumn,
                                        numberFormat: props.isRounded ? '0.00' : undefined,
                                    }),
                            },
                            ragColumn: props.isRagColumn,
                            lockVisibility: true,
                            filter: 'numeric',
                            sortable: true,
                            resizable: props.resizable,
                            multiValueTypeColumn: true,
                            multiValueExportTitles: props.isRagColumn
                                ? {
                                      comparisonValue: comparisonDatasetDate
                                          ? `${comparisonDatasetDate} ${t('exportColumns.value')}`
                                          : t('exportColumns.comparisonValue'),
                                      comparisonColour: comparisonDatasetDate
                                          ? `${comparisonDatasetDate} ${t('exportColumns.colour')}`
                                          : t('exportColumns.comparisonColour'),
                                      valueInFocus: datasetDateInFocus
                                          ? `${datasetDateInFocus} ${t('exportColumns.value')}`
                                          : t('exportColumns.valueInFocus'),
                                      colourInFocus: datasetDateInFocus
                                          ? `${datasetDateInFocus} ${t('exportColumns.colour')}`
                                          : t('exportColumns.colourInFocus'),
                                      changeDirection: t('exportColumns.changeDirection'),
                                  }
                                : {
                                      comparisonValue: comparisonDatasetDate
                                          ? `${comparisonDatasetDate} ${t('exportColumns.value')}`
                                          : t('exportColumns.comparisonValue'),
                                      valueInFocus: datasetDateInFocus
                                          ? `${datasetDateInFocus} ${t('exportColumns.value')}`
                                          : t('exportColumns.valueInFocus'),
                                      changeDirection: t('exportColumns.changeDirection'),
                                  },
                        },
                        {
                            id: props.id ? `${props.id}_${props.valueField}_change` : `${props.valueField}_change`,
                            field: props.id ? `${props.id}.${props.valueField}.change` : `${props.valueField}.change`,
                            title: t('columns.change'),
                            show: props.show,
                            minWidth: 140,
                            customColumn: true,
                            lockVisibility: true,
                            format: props.isRounded ? '{0:0.00}' : undefined,
                            filter: 'numeric',
                            resizable: props.resizable,
                        },
                    ],
                },
            ];
        }
        return [
            {
                id: props.id ? `${props.id}_${props.valueField}` : `${props.valueField}`,
                field: props.id ? `${props.id}.${props.valueField}` : props.valueField,
                title: props.title ?? t(`columns.${props.valueField}`),
                show: props.show,
                locked: props.locked,
                minWidth: props.singleColumnMinWidth,
                format: props.isRounded ? '{0:0.00}' : undefined,
                filter: props.isNumeric ? 'numeric' : props.isDate ? 'date' : undefined,
                filterKey: props.isRagColumn ? 'rateOrScore' : undefined,
                ragColumn: props.isRagColumn,
                filterDisabled: props.filterDisabled,
                lockWidth: props.lockWidth,
                filterClassName: props.filterClassName,
                filterable: props.isFilterable,
                resizable: props.resizable,
                headerClassName: props.headerClassName,
                className: props.className,
                cellName: props.isDate ? 'DateCell' : undefined,
                cells: {
                    data: props.isRagColumn
                        ? (cellProps) => RagValueCell({ ...cellProps, numberFormat: '0.00' })
                        : props.isDate
                          ? DateCell
                          : props.maxLength
                            ? MaxLengthCell(props.maxLength)
                            : undefined,
                },
                multiValueExportTitles: props.isRagColumn
                    ? {
                          rateOrScore: t('exportColumns.value'),
                          colour: t('exportColumns.colour'),
                      }
                    : undefined,
            },
        ];
    };

    const tableInfo = useMemo(() => {
        if (!data) return undefined;

        const info: ITableInfo = {
            columns: [],
            data: [],
        };

        info.columns.push({
            id: 'popOutPage',
            cells: {
                data: (props) => {
                    const row = props.dataItem as IRow;
                    return (
                        <CellWithChildren {...props}>
                            <button
                                aria-label="Pop Out Page"
                                title={t('tooltips.viewOnSiteHub')}
                                data-test-id="pop-out-button"
                                type="button"
                                className={styles.popOutPageGrid}
                                onClick={() => {
                                    PopOutPage(
                                        user?.organisationKey as string,
                                        `${siteHubRoute}?sitecode=${row.siteNumber}`,
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={['far', 'arrow-up-right-from-square']} />
                            </button>
                        </CellWithChildren>
                    );
                },
            },
            customColumn: true,
            width: 50,
            filterable: false,
            resizable: false,
            sortable: false,
            locked: true,
        });

        info.columns.push({
            id: 'screen_filterable_columns',
            title: isFiltered ? t('columns.screenFiltered') : t('columns.unfiltered'),
            sortable: false,
            filterable: false,
            resizable: false,
            locked: true,
            lockWidth: true,
            headerClassName: isFiltered
                ? cn(gridStyles.screenFilteredHeader, gridStyles.screenFilteredGroupHeader)
                : undefined,
            children: [
                ...getTableColumn({
                    valueField: 'siteName',
                    locked: true,
                    headerClassName: isFiltered
                        ? cn(gridStyles.screenFilteredHeader, gridStyles.screenFilteredFirst)
                        : undefined,
                    className: isFiltered ? cn(gridStyles.screenFilteredFirst) : undefined,
                    filterDisabled: true,
                    filterClassName: isFiltered ? cn(gridStyles.screenFilteredFirst) : undefined,
                    resizable: false,
                    lockWidth: true,
                    maxLength: 14,
                }),
                ...getTableColumn({
                    valueField: 'siteStatus',
                    locked: true,
                    singleColumnMinWidth: comparisonDatasetId !== undefined ? 100 : 120,
                    headerClassName: isFiltered ? cn(gridStyles.screenFilteredHeader) : undefined,
                    filterDisabled: true,
                    resizable: false,
                    lockWidth: true,
                    maxLength: 14,
                }),
                ...getTableColumn({
                    valueField: 'country',
                    locked: true,
                    headerClassName: isFiltered
                        ? cn(gridStyles.screenFilteredHeader, gridStyles.screenFilteredLast)
                        : undefined,
                    className: isFiltered ? cn(gridStyles.screenFilteredLast) : undefined,
                    filterDisabled: true,
                    resizable: false,
                    filterClassName: isFiltered ? cn(gridStyles.screenFilteredLast) : undefined,
                    lockWidth: true,
                    maxLength: 14,
                }),
            ],
        });

        info.columns.push(
            ...getTableColumn({
                valueField: 'siteScore',
                isNumeric: true,
                isRagColumn: true,
                singleColumnMinWidth: 140,
                multiColumnMinWidth: 140,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.score ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'greenKriCount',
                isNumeric: true,
                isRounded: false,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.numGreenKRIs ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'amberKriCount',
                isNumeric: true,
                isRounded: false,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.numAmberKRIs ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'redKriCount',
                isNumeric: true,
                isRounded: false,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.numRedKRIs ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'greenDecisionMakingKriCount',
                isNumeric: true,
                isRounded: false,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.numGreenDecisionMakingKRIs ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'amberDecisionMakingKriCount',
                isNumeric: true,
                isRounded: false,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.numAmberDecisionMakingKRIs ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'redDecisionMakingKriCount',
                isNumeric: true,
                isRounded: false,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.numRedDecisionMakingKRIs ?? false,
            }),
        );

        info.columns.push(
            ...getTableColumn({
                valueField: 'activeDate',
                isDate: true,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.activeDate ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'closedDate',
                isDate: true,
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.closedDate ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'investigatorName',
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.investigator ?? false,
            }),
        );
        info.columns.push(
            ...getTableColumn({
                valueField: 'monitor',
                show: siteFieldsConfigData?.getStudyHubSiteFieldsConfig?.monitor ?? false,
            }),
        );

        data.studyHubSiteGridPanelColumnData?.forEach((column: StudyHubSiteGridPanelColumnData) => {
            info.columns.push({
                id: `panel_${column.reference}`,
                title: `${column.plotTitle}`,
                sortable: false,
                filterable: false,
                children: [
                    ...getTableColumn({
                        id: `panel_${column.reference}`,
                        valueField: 'yAxisPlot',
                        title: column.yAxisLabel || t('columns.defaultYAxisPlot'),
                        isNumeric: true,
                    }),
                    ...getTableColumn({
                        id: `panel_${column.reference}`,
                        valueField: 'yAxisCount',
                        title: column.yVolumeLabel || t('columns.defaultVolumePlot'),
                        isNumeric: true,
                    }),
                    ...getTableColumn({
                        id: `panel_${column.reference}`,
                        valueField: 'xAxisCount',
                        title: column.xAxisLabel,
                        isNumeric: true,
                    }),
                    ...getTableColumn({
                        id: `panel_${column.reference}`,
                        valueField: 'score',
                        isNumeric: true,
                        isRagColumn: true,
                    }),
                ],
            });
        });

        data.studyHubSiteGridWidgetColumnData?.forEach((column: StudyHubSiteGridWidgetColumnData) => {
            info.columns.push(
                ...getTableColumn({
                    id: `widget_${column.reference}`,
                    valueField: 'widget',
                    title: `${column.displayName}`,
                    isNumeric: true,
                    isRounded: (column.isFloat as boolean) ?? true,
                }),
            );
        });

        data.studyHubSiteGridData?.forEach((studyHubSiteGridData: StudyHubSiteGridData) => {
            const flattenedRow: { [key: string]: unknown } = {
                reference: studyHubSiteGridData.siteReference,
                siteNumber: studyHubSiteGridData.siteNumber,
                siteName: studyHubSiteGridData.siteName,
                siteStatus: studyHubSiteGridData.siteStatus,
                siteScore:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: studyHubSiteGridData.siteScore?.comparisonValue,
                              valueInFocus: studyHubSiteGridData.siteScore?.inFocusValue,
                              change: studyHubSiteGridData.siteScore?.change,
                              changeDirection: studyHubSiteGridData.siteScore?.direction,
                              comparisonColour: studyHubSiteGridData.siteScore?.comparisonColour,
                              colourInFocus: studyHubSiteGridData.siteScore?.inFocusColour,
                          }
                        : {
                              rateOrScore: studyHubSiteGridData.siteScore?.inFocusValue,
                              colour: studyHubSiteGridData.siteScore?.inFocusColour,
                          },
                country: studyHubSiteGridData.country,
                activeDate: studyHubSiteGridData.activeDate,
                closedDate: studyHubSiteGridData.closedDate,
                monitor: studyHubSiteGridData.monitor,
                investigatorName: studyHubSiteGridData.investigatorName,
                greenKriCount:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: studyHubSiteGridData.greenKriCount?.comparisonValue,
                              valueInFocus: studyHubSiteGridData.greenKriCount?.inFocusValue,
                              change: studyHubSiteGridData.greenKriCount?.change,
                              changeDirection: studyHubSiteGridData.greenKriCount?.direction,
                          }
                        : studyHubSiteGridData.greenKriCount?.inFocusValue,
                amberKriCount:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: studyHubSiteGridData.amberKriCount?.comparisonValue,
                              valueInFocus: studyHubSiteGridData.amberKriCount?.inFocusValue,
                              change: studyHubSiteGridData.amberKriCount?.change,
                              changeDirection: studyHubSiteGridData.amberKriCount?.direction,
                          }
                        : studyHubSiteGridData.amberKriCount?.inFocusValue,
                redKriCount:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: studyHubSiteGridData.redKriCount?.comparisonValue,
                              valueInFocus: studyHubSiteGridData.redKriCount?.inFocusValue,
                              change: studyHubSiteGridData.redKriCount?.change,
                              changeDirection: studyHubSiteGridData.redKriCount?.direction,
                          }
                        : studyHubSiteGridData.redKriCount?.inFocusValue,
                greenDecisionMakingKriCount:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: studyHubSiteGridData.greenDecisionMakingKriCount?.comparisonValue,
                              valueInFocus: studyHubSiteGridData.greenDecisionMakingKriCount?.inFocusValue,
                              change: studyHubSiteGridData.greenDecisionMakingKriCount?.change,
                              changeDirection: studyHubSiteGridData.greenDecisionMakingKriCount?.direction,
                          }
                        : studyHubSiteGridData.greenDecisionMakingKriCount?.inFocusValue,
                amberDecisionMakingKriCount:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: studyHubSiteGridData.amberDecisionMakingKriCount?.comparisonValue,
                              valueInFocus: studyHubSiteGridData.amberDecisionMakingKriCount?.inFocusValue,
                              change: studyHubSiteGridData.amberDecisionMakingKriCount?.change,
                              changeDirection: studyHubSiteGridData.amberDecisionMakingKriCount?.direction,
                          }
                        : studyHubSiteGridData.amberDecisionMakingKriCount?.inFocusValue,
                redDecisionMakingKriCount:
                    comparisonDatasetId !== undefined
                        ? {
                              comparisonValue: studyHubSiteGridData.redDecisionMakingKriCount?.comparisonValue,
                              valueInFocus: studyHubSiteGridData.redDecisionMakingKriCount?.inFocusValue,
                              change: studyHubSiteGridData.redDecisionMakingKriCount?.change,
                              changeDirection: studyHubSiteGridData.redDecisionMakingKriCount?.direction,
                          }
                        : studyHubSiteGridData.redDecisionMakingKriCount?.inFocusValue,
            };

            studyHubSiteGridData.studyHubSiteGridPanelDataList
                ?.filter((studyHubSiteGridPanelData) => studyHubSiteGridPanelData !== null)
                .map((studyHubSiteGridPanelData: StudyHubSiteGridPanelData) => {
                    flattenedRow[`panel_${studyHubSiteGridPanelData.reference}`] = {
                        yAxisPlot:
                            comparisonDatasetId !== undefined
                                ? {
                                      comparisonValue: studyHubSiteGridPanelData.yAxisPlot?.comparisonValue,
                                      valueInFocus: studyHubSiteGridPanelData.yAxisPlot?.inFocusValue,
                                      change: studyHubSiteGridPanelData.yAxisPlot?.change,
                                      changeDirection: studyHubSiteGridPanelData.yAxisPlot?.direction,
                                  }
                                : studyHubSiteGridPanelData.yAxisPlot?.inFocusValue,
                        yAxisCount:
                            comparisonDatasetId !== undefined
                                ? {
                                      comparisonValue: studyHubSiteGridPanelData.yAxisCount?.comparisonValue,
                                      valueInFocus: studyHubSiteGridPanelData.yAxisCount?.inFocusValue,
                                      change: studyHubSiteGridPanelData.yAxisCount?.change,
                                      changeDirection: studyHubSiteGridPanelData.yAxisCount?.direction,
                                  }
                                : studyHubSiteGridPanelData.yAxisCount?.inFocusValue,
                        xAxisCount:
                            comparisonDatasetId !== undefined
                                ? {
                                      comparisonValue: studyHubSiteGridPanelData.xAxisCount?.comparisonValue,
                                      valueInFocus: studyHubSiteGridPanelData.xAxisCount?.inFocusValue,
                                      change: studyHubSiteGridPanelData.xAxisCount?.change,
                                      changeDirection: studyHubSiteGridPanelData.xAxisCount?.direction,
                                  }
                                : studyHubSiteGridPanelData.xAxisCount?.inFocusValue,
                        score:
                            comparisonDatasetId !== undefined
                                ? {
                                      comparisonValue: studyHubSiteGridPanelData.score?.comparisonValue,
                                      valueInFocus: studyHubSiteGridPanelData.score?.inFocusValue,
                                      change: studyHubSiteGridPanelData.score?.change,
                                      changeDirection: studyHubSiteGridPanelData.score?.direction,
                                      comparisonColour: studyHubSiteGridPanelData.score?.comparisonColour,
                                      colourInFocus: studyHubSiteGridPanelData.score?.inFocusColour,
                                  }
                                : {
                                      rateOrScore: studyHubSiteGridPanelData.score?.inFocusValue,
                                      colour: studyHubSiteGridPanelData.score?.inFocusColour,
                                  },
                    };
                    return flattenedRow;
                });

            studyHubSiteGridData.studyHubSiteGridWidgetData
                ?.filter((studyHubSiteGridWidgetData) => studyHubSiteGridWidgetData !== null)
                .map((studyHubSiteGridWidgetData: StudyHubSiteGridWidgetData) => {
                    flattenedRow[`widget_${studyHubSiteGridWidgetData.reference}`] = {
                        widget:
                            comparisonDatasetId !== undefined
                                ? {
                                      comparisonValue: studyHubSiteGridWidgetData?.comparisonValue,
                                      valueInFocus: studyHubSiteGridWidgetData?.inFocusValue,
                                      change: studyHubSiteGridWidgetData?.change,
                                      changeDirection: studyHubSiteGridWidgetData?.direction,
                                  }
                                : studyHubSiteGridWidgetData?.inFocusValue,
                    };
                    return flattenedRow;
                });

            info.data.push(flattenedRow);
        });

        return info;
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    if (!data || isLoading || error) {
        return (
            <Card data-testid="site-view-loading-card" title={t('title')}>
                {error ? <ErrorMessage /> : <Loading />}
            </Card>
        );
    }

    const pageSize = 60;

    const pageChange = (event: GridPageChangeEvent) => {
        setSkip(event.page.skip);
    };

    const dataLength = tableInfo?.data?.length ?? 0;
    const columnsLength = tableInfo?.columns
        ? tableInfo.columns.reduce((sum, col) => {
              if (Array.isArray(col.children) && col.children.length > 0) {
                  // Recursively count child columns
                  const countChildren = (children: GridColumnProps[]): number =>
                      children.reduce((childSum, childCol) => {
                          if (Array.isArray(childCol.children) && childCol.children.length > 0) {
                              return childSum + countChildren(childCol.children);
                          }
                          return childSum + 1;
                      }, 0);
                  return sum + countChildren(col.children);
              }
              return sum + 1;
          }, 0)
        : 0;

    const virtualizationProps =
        dataLength * columnsLength > 1800
            ? {
                  columnVirtualization: true,
                  pageSize,
                  skip,
                  total: dataLength,
                  onPageChange: pageChange,
                  scrollable: 'virtual' as const,
                  rowHeight: 28,
              }
            : {};

    const exportFileName = comparisonDatasetDate
        ? `Site View_${studyName}_Dataset(s) ${comparisonDatasetDate} - ${datasetDateInFocus}`
        : `Site View_${studyName}_Dataset(s) ${datasetDateInFocus}`;

    return (
        <Grid
            dataItemKey="siteName"
            title={t('title')}
            maxHeight="auto"
            testDataId="site-overview-grid"
            data={tableInfo?.data ?? []}
            columns={tableInfo?.columns ?? []}
            loading={isLoading}
            useLoadingOverlay
            reorderable={false}
            exportFilename={exportFileName}
            gridSettingsMenuDisabled
            showAddIcon={false}
            service={AuditServiceType.CENTRAL_MONITORING_2}
            area={AuditApplicationArea.CENTRAL_MONITORING_2}
            dataType={AuditDataType.EXCEL_EXPORT} // The data doesn't have an OPRA type so just use this
            auditDescriptionPrefix="CM - Study Hub"
            {...virtualizationProps}
        />
    );
};

export default SiteView;
